/**
 * @CODE:SPEC-ACC-002-API-TRAIL-001
 * Trail-based Accommodation Search API
 *
 * Provides functions for searching accommodations based on trails and courses:
 * - getCoursesForTrail: Fetch all courses for a specific trail
 * - searchAccommodationsByCourse: Find accommodations near a course
 * - searchAccommodationsByTrail: Find accommodations near any course in a trail
 */

import { getSupabaseClient } from "@/lib/supabase";
import type {
  AccommodationWithDetails,
  AccommodationFilters,
  Course,
} from "../types/accommodation.types";

/**
 * Fetch all courses for a specific trail
 *
 * @param trail_id - Trail ID (e.g., 'T_THEME_MNG0000011235' for 해파랑길)
 * @returns Array of courses in the trail, sorted by course number
 *
 * @example
 * ```ts
 * const courses = await getCoursesForTrail('T_THEME_MNG0000011235');
 * // Returns: [{ course_id: '...', name: '해파랑길 1코스', ... }, ...]
 * ```
 */
export async function getCoursesForTrail(trail_id: string): Promise<Course[]> {
  const client = getSupabaseClient();
  const { data, error } = await client
    .from("courses")
    .select(
      `
      course_id,
      trail_id,
      course_number,
      name,
      length,
      estimated_time,
      difficulty
    `
    )
    .eq("trail_id", trail_id)
    .order("course_number", { ascending: true });

  if (error) {
    console.error("Error fetching courses for trail:", error);
    throw new Error(`Failed to fetch courses: ${error.message}`);
  }

  return data || [];
}

/**
 * Search accommodations near a specific course
 *
 * Uses PostGIS ST_Distance to calculate distance from course route to accommodation.
 * Results are sorted by distance (closest first) by default.
 *
 * @param course_id - Course ID to search near
 * @param filters - Optional filters (distance, type, price, pagination)
 * @returns Array of accommodations with distance information
 *
 * @example
 * ```ts
 * const accommodations = await searchAccommodationsByCourse(
 *   'T_CRS_MNG0000004239',
 *   {
 *     distance_from_course_km: 5,
 *     accommodation_type_id: 1,
 *     page: 1,
 *     limit: 20
 *   }
 * );
 * ```
 */
export async function searchAccommodationsByCourse(filters: {
  courseId: string;
  maxDistanceKm?: number;
  accommodationTypeId?: number;
  minPrice?: number;
  maxPrice?: number;
  hasPickup?: boolean;
}): Promise<{ data: AccommodationWithDetails[]; count: number }> {
  const {
    courseId,
    maxDistanceKm = 10,
    accommodationTypeId,
    minPrice,
    maxPrice,
    hasPickup,
  } = filters;

  const client = getSupabaseClient();
  const { data, error } = await client.rpc("get_accommodations_near_course", {
    p_course_id: courseId,
    p_max_distance_km: maxDistanceKm,
    p_accommodation_type_id: accommodationTypeId || null,
    p_min_price: minPrice || null,
    p_max_price: maxPrice || null,
    p_has_pickup: hasPickup || null,
  });

  if (error) {
    console.error("Error searching accommodations by course:", error);
    throw new Error(`Failed to search accommodations: ${error.message}`);
  }

  // Transform database response to match AccommodationWithDetails type
  const accommodations =
    data?.map((item: any) => ({
      accommodation_id: item.id,
      name: item.name,
      type_legacy: item.type_legacy,
      description: item.description,
      province: item.province,
      district: item.district,
      address: item.address,
      latitude: item.latitude,
      longitude: item.longitude,
      price_from: item.price_from,
      accommodation_type: {
        id: item.accommodation_type_id,
        name: item.type_legacy,
      },
      main_image: {
        image_id: 0,
        accommodation_id: item.id,
        image_url: item.main_image_url || null,
        is_main_photo: true,
      },
      distance_km: item.distance_km,
    })) || [];

  return {
    data: accommodations,
    count: accommodations.length,
  };
}

/**
 * Search accommodations near any course in a trail
 *
 * Finds accommodations within specified distance of ANY course in the trail,
 * and returns the nearest course information for each accommodation.
 *
 * @param trail_id - Trail ID to search accommodations for
 * @param filters - Optional filters (distance, type, price, pagination)
 * @returns Array of accommodations with nearest course information
 *
 * @example
 * ```ts
 * const accommodations = await searchAccommodationsByTrail(
 *   'T_THEME_MNG0000011235', // 해파랑길
 *   {
 *     distance_from_course_km: 10,
 *     priceFrom: 50000,
 *     priceTo: 150000
 *   }
 * );
 * // Each result includes: distance_km, nearest_course: { course_id, course_name }
 * ```
 */
export async function searchAccommodationsByTrail(filters: {
  trailId: string;
  maxDistanceKm?: number;
  accommodationTypeId?: number;
  minPrice?: number;
  maxPrice?: number;
  hasPickup?: boolean;
}): Promise<{ data: AccommodationWithDetails[]; count: number }> {
  const {
    trailId,
    maxDistanceKm = 10,
    accommodationTypeId,
    minPrice,
    maxPrice,
    hasPickup,
  } = filters;

  const client = getSupabaseClient();
  const { data, error } = await client.rpc("get_accommodations_by_trail", {
    p_trail_id: trailId,
    p_max_distance_km: maxDistanceKm,
    p_accommodation_type_id: accommodationTypeId || null,
    p_min_price: minPrice || null,
    p_max_price: maxPrice || null,
    p_has_pickup: hasPickup || null,
  });

  if (error) {
    console.error("Error searching accommodations by trail:", error);
    throw new Error(`Failed to search accommodations: ${error.message}`);
  }

  // Transform database response to match AccommodationWithDetails type
  const accommodations =
    data?.map((item: any) => ({
      accommodation_id: item.id,
      name: item.name,
      type_legacy: item.type_legacy,
      description: item.description,
      province: item.province,
      district: item.district,
      address: item.address,
      latitude: item.latitude,
      longitude: item.longitude,
      price_from: item.price_from,
      accommodation_type: {
        id: item.accommodation_type_id,
        name: item.type_legacy,
      },
      main_image: {
        image_id: 0,
        accommodation_id: item.id,
        image_url: item.main_image_url || null,
        is_main_photo: true,
      },
      distance_km: item.distance_km,
      nearest_course: {
        course_id: item.nearest_course_id,
        course_name: item.nearest_course_name,
        distance_km: item.distance_km,
      },
    })) || [];

  return {
    data: accommodations,
    count: accommodations.length,
  };
}
